import {Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs";
import {
  ClientOrgPreferencesData,
  ClientsPreferences,
  ClientUserPreferencesData
} from "../../models/client-storage.model";
import {FirebaseOrgBranding, OrgBranding} from "../../models/branding.model";
import {LeftSideItemName} from "../../../core/models/nav-item.model";

@Injectable({
  providedIn: 'root'
})
export class LoadClientStorageService {
  private notifyDynamicDashboardPreferences: BehaviorSubject<ClientOrgPreferencesData<any> | boolean> = new BehaviorSubject(null);
  notifyDynamicDashboardPreferencesObservable$ = this.notifyDynamicDashboardPreferences.asObservable();

  private notifyOrgPreferences: BehaviorSubject<ClientsPreferences<any> | boolean> = new BehaviorSubject(null);
  notifyOrgObservable$ = this.notifyOrgPreferences.asObservable();

  protected notifyUserPreferences: BehaviorSubject<ClientUserPreferencesData | boolean> = new BehaviorSubject<ClientUserPreferencesData | boolean>(null);
  notifyUserPreferencesAsObservable$ = this.notifyUserPreferences.asObservable();

  private notifyBrandingSelectionLoaded: BehaviorSubject<ClientsPreferences<any> | boolean> = new BehaviorSubject<ClientsPreferences<any> | boolean>(null);
  notifyBrandingSelectionLoadedAsObservable$ = this.notifyBrandingSelectionLoaded.asObservable();

  constructor() {
  }

  /**
   * Save dynamic dashboard preferences
   * @param preferences
   */
  storeDynamicDashboardPreferences(preferences: any) {
    this.notifyDynamicDashboardPreferences.next(preferences);
  }

  /**
   * Save org preferences
   * @param preferences
   */
  storeOrgPreferences(preferences: ClientsPreferences<any> | boolean) {
    this.notifyOrgPreferences.next(preferences);
  }

  storeOrgBrandingLoaded(clientSelection: ClientsPreferences<any> | boolean) {
    this.notifyBrandingSelectionLoaded.next(clientSelection)
  }

  storeUserPreferencesLoaded(clientSelection: ClientUserPreferencesData | boolean) {
    this.notifyUserPreferences.next(clientSelection)
  }
}

/**
 * Default Netop org preferences (including and using for branding)
 */
export const NETOP_DEFAULT_ORG_PREFERENCES: FirebaseOrgBranding = {
  preferences: {
    isOwnPreferences: false,
    data: {
      orgBranding: new OrgBranding(
        {
          leftMenuBackgroundColor: "#264d70",
          leftMenuForegroundColor: "#ffffff",
          topMenuBackgroundColor: "#467fca",
          topMenuForegroundColor: "#ffffff",
          dialogHeaderBackgroundColor: "#193951",
          dialogHeaderForegroundColor: "#ffffff"
        },
        "Netop",
        "primary",
        {
          file: null,
          url: "data:image/svg+xml;base64,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"
        },
      ),
      navList: [
        {[LeftSideItemName.DASHBOARD]: true},
        {[LeftSideItemName.FABRICS]: true},
        {[LeftSideItemName.VENUES]: true},
        {[LeftSideItemName.DEVICES]: true},
        {[LeftSideItemName.ALARMS]: true},
        {[LeftSideItemName.SETTINGS]: true},
        {[LeftSideItemName.OPERATIONS]: true},
        {[LeftSideItemName.ACCOUNTS]: true},
        {[LeftSideItemName.SERVICES]: true},
        {[LeftSideItemName.REPORTS]: true},
      ]
    }
  }
}
